import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Briefcase, ArrowRight, GraduationCap, FileCheck2, Globe2 } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Icon, Eyebrow } from "./publicUI";
import { INDUSTRIES, IMG } from "./siteContent";

const STEPS = [
  { icon: Search, t: "Find a role", d: "Browse live international openings by position, country and skill." },
  { icon: FileCheck2, t: "Register & apply", d: "Create your candidate profile and apply in minutes — no cost to you." },
  { icon: GraduationCap, t: "Get job-ready", d: "Complete trade testing and pre-deployment orientation with Emira." },
  { icon: Globe2, t: "Get deployed", d: "We manage medical, visa and travel through to joining abroad." },
];

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => { client.get("/public/jobs").then((r) => setJobs((r.data.items || r.data || []).slice(0, 6))).catch(() => {}); }, []);

  return (
    <div>
      <section className="relative brand-gradient-navy overflow-hidden">
        <div className="absolute inset-0 brand-dotgrid opacity-50" />
        <img src={IMG.scaffolding} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300 mb-3">Careers</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] max-w-3xl">Build your international career with Emira</h1>
          <p className="mt-5 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Connecting India to global careers. Explore live openings across the Gulf, Europe and beyond — and let our team guide you from application to deployment.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 max-w-xl">
            <div className="flex-1 flex items-center gap-2 px-3 bg-white rounded-xl">
              <Search className="h-5 w-5 text-slate-400" />
              <input data-testid="careers-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && nav(`/jobs?q=${encodeURIComponent(q)}`)}
                placeholder="Search by position, country or skill..." className="flex-1 bg-transparent py-3 outline-none text-sm" />
            </div>
            <Button data-testid="careers-search-btn" onClick={() => nav(`/jobs?q=${encodeURIComponent(q)}`)} className="bg-[#0088FF] hover:bg-[#0070E0] h-12 px-8">Search Jobs</Button>
          </div>
        </div>
      </section>

      {/* LATEST JOBS */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-2xl"><Eyebrow>Open Positions</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Latest opportunities</h2></div>
          <Button variant="outline" data-testid="careers-view-all" onClick={() => nav("/jobs")}>View all jobs <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>
        {jobs.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center text-slate-500">
            New openings are published regularly. <button onClick={() => nav("/jobs")} className="text-[#0088FF] font-medium">Browse all jobs</button>.
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((j, i) => (
              <motion.button key={j.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                onClick={() => nav(`/jobs/${j.id}`)} data-testid={`career-job-${j.id}`}
                className="text-left bg-white rounded-2xl border border-slate-200 p-6 hover-lift">
                <div className="h-11 w-11 rounded-xl bg-[#0088FF]/10 grid place-items-center text-[#0088FF] mb-4"><Briefcase className="h-5 w-5" /></div>
                <h3 className="font-display font-semibold text-[#0A192F]">{j.title}</h3>
                <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-2"><MapPin className="h-4 w-4" /> {j.country || j.location || "International"}</div>
                {j.salary && <div className="text-sm font-medium text-[#0A192F] mt-1">{j.salary}</div>}
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-[#0088FF] font-medium">View &amp; apply <ArrowRight className="h-4 w-4" /></div>
              </motion.button>
            ))}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl"><Eyebrow>How It Works</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">From application to deployment</h2></div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-slate-200 p-6 hover-lift">
                <div className="h-11 w-11 rounded-xl bg-[#0A192F] grid place-items-center text-white mb-4"><s.icon className="h-5 w-5" /></div>
                <h3 className="font-semibold text-[#0A192F]">{s.t}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ROLE CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl"><Eyebrow>Job Categories</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">We hire across trades &amp; sectors</h2></div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map((ind) => (
            <button key={ind.title} onClick={() => nav(`/jobs?q=${encodeURIComponent(ind.title.split(" ")[0])}`)}
              className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4 hover-lift text-left">
              <div className="h-10 w-10 rounded-lg bg-[#0088FF]/10 grid place-items-center text-[#0088FF] shrink-0"><Icon name={ind.icon} className="h-5 w-5" /></div>
              <span className="text-sm font-medium text-[#0A192F] leading-snug">{ind.title}</span>
            </button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="brand-gradient-navy rounded-3xl p-10 lg:p-14 relative overflow-hidden flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
          <div className="absolute inset-0 brand-dotgrid opacity-40" />
          <div className="relative max-w-xl">
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-white">Ready to work abroad?</h2>
            <p className="text-slate-300 mt-2">Register your candidate profile and apply to international roles in minutes.</p>
          </div>
          <div className="relative flex flex-col sm:flex-row gap-3">
            <Button size="lg" data-testid="careers-register-btn" onClick={() => nav("/register")} className="bg-[#0088FF] hover:bg-[#0070E0] h-12">Register as Candidate</Button>
            <Button size="lg" variant="outline" data-testid="careers-browse-btn" onClick={() => nav("/jobs")} className="h-12 bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">Browse Jobs</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
